// The program's responsibility matrix: for one contract period, which team
// owes, sees, copies or inherits each deliverable, composed from the reads
// the team pages already make (ListDuties rules + detail, ListWorkstreams
// detail, ListOccurrences for the period). The run scorer (ui-cli `matrix`,
// tools/ui-cli/internal/respmatrix) classifies the same way: keep the two in
// lockstep.
/** The order kinds read in, strongest obligation first. */ export const MATRIX_KINDS = [
    'owes',
    'copy',
    'inherited',
    'sees',
    'not_owed'
];
const key = (team, duty)=>`${team}|${duty}`;
/** A SOW is in force in the window when its PoP overlaps it (no end = open). */ function inForce(s, w) {
    return (!s.popStart || !w.end || s.popStart <= w.end) && (!s.popEnd || !w.start || s.popEnd >= w.start);
}
/**
 * Every (team, deliverable) the period touches, classified:
 * - owes: the team's SOW rule dates it, the team owns its line, or it is the
 *   contract-wide row; or the SOW performs it with no date this period;
 * - copy: the SOW routes it to the base scope and the team holds its own
 *   dated copy (M5);
 * - inherited: an optional task's award rule dates it for the team, no SOW
 *   rule of its own;
 * - sees: the SOW routed it to another team (or the base scope), whose
 *   dates it sees read-only;
 * - not_owed: the SOW says the team does not owe it.
 */ export function buildResponsibilityMatrix(input) {
    const duties = new Set(input.dutyIds);
    const ruleById = new Map(input.rules.map((r)=>[
            r.id,
            r
        ]));
    const sowTeam = new Map();
    for (const s of input.sows)if (inForce(s, input.window)) sowTeam.set(s.id, s);
    // The latest-starting in-force SOW's handling wins per team.
    const asg = new Map();
    for (const a of input.assignments){
        const s = sowTeam.get(a.sowId);
        if (!s || !duties.has(a.dutyId)) continue;
        const k = key(s.workstreamId, a.dutyId);
        const start = s.popStart ?? '';
        const cur = asg.get(k);
        if (!cur || start > cur.start || start === cur.start && a.id > cur.a.id) asg.set(k, {
            a,
            start
        });
    }
    const dates = new Map();
    for (const o of input.occurrences){
        if (o.retired || o.event?.projected || !duties.has(o.dutyId)) continue;
        const k = key(o.workstreamId ?? '', o.dutyId);
        const arr = dates.get(k) ?? [];
        arr.push(o);
        dates.set(k, arr);
    }
    for (const arr of dates.values())arr.sort((a, b)=>a.dueOn.localeCompare(b.dueOn));
    const teamRule = new Map();
    const awardRule = new Map();
    for (const r of input.rules){
        if (r.assignmentId) {
            if (!teamRule.has(r.assignmentId)) teamRule.set(r.assignmentId, r);
        } else if (!awardRule.has(r.dutyId)) awardRule.set(r.dutyId, r);
    }
    const sourceOf = (rule, a)=>({
            quote: a?.overrideLocators?.find((l)=>l.quote)?.quote || rule?.ruleText || undefined,
            locator: rule?.sourceLocator || a?.sourceLocator || undefined
        });
    const cells = [];
    for (const [k, ds] of dates){
        const [teamId, dutyId] = k.split('|');
        const a = asg.get(k)?.a;
        const rules = ds.map((o)=>o.ruleId ? ruleById.get(o.ruleId) : undefined);
        let kind = 'owes';
        if (teamId && !rules.some((r)=>r?.assignmentId)) {
            if (a?.handling === 'routed' && !a.routedToWorkstreamId) kind = 'copy';
            else if (rules.some((r)=>r?.optionalTaskId)) kind = 'inherited';
        }
        const rule = rules.find(Boolean);
        cells.push({
            teamId,
            dutyId,
            kind,
            rule,
            dates: ds,
            ...sourceOf(rule, a)
        });
    }
    const watchOwner = new Map();
    for (const w of input.watches)if (!watchOwner.has(key(w.watcherWorkstreamId, w.dutyId))) watchOwner.set(key(w.watcherWorkstreamId, w.dutyId), w.ownerWorkstreamId);
    for (const [k, { a }] of asg){
        if (dates.has(k)) continue;
        const [teamId, dutyId] = k.split('|');
        if (a.handling === 'routed') {
            const owner = watchOwner.get(k) ?? a.routedToWorkstreamId ?? '';
            const ownerDates = dates.get(key(owner, dutyId)) ?? [];
            const rule = ownerDates[0]?.ruleId && ruleById.get(ownerDates[0].ruleId) || undefined;
            cells.push({
                teamId,
                dutyId,
                kind: 'sees',
                ownerTeamId: owner,
                rule,
                dates: ownerDates,
                ...sourceOf(rule, a)
            });
        } else if (a.handling === 'not_owed') {
            cells.push({
                teamId,
                dutyId,
                kind: 'not_owed',
                dates: [],
                notOwedReason: a.notOwedReason || undefined,
                ...sourceOf(undefined, a)
            });
        } else {
            const rule = teamRule.get(a.id) ?? awardRule.get(dutyId);
            cells.push({
                teamId,
                dutyId,
                kind: 'owes',
                rule,
                dates: [],
                ...sourceOf(rule, a)
            });
        }
    }
    return cells;
}
/** The date a cell points at: the first one on or after today, else the last. */ export function nextDate(cell, today) {
    return cell.dates.find((o)=>o.dueOn >= today)?.dueOn ?? cell.dates[cell.dates.length - 1]?.dueOn;
}
/** Cells per kind. */ export function countKinds(cells) {
    const out = {
        owes: 0,
        copy: 0,
        inherited: 0,
        sees: 0,
        not_owed: 0
    };
    for (const c of cells)out[c.kind]++;
    return out;
}
